





 
import TaskSubtasks from "./TaskSubtasks";
import TaskActivity2 from "./TaskActivity2";
import { updateTaskStatusApi } from "../../app/api";
import { useEffect, useState } from "react";
import TaskComments from "./TaskComments";
import LateCompletionModal from "./LateCompletionModal";
import HoldReasonModal from "./HoldReasonModal";
import { createPortal } from "react-dom";

import { CalendarRange, Flag, ListChecks, X } from "lucide-react";
import { formatDisplayDate, getDateInputValue } from "../../utils/dateFormat";
 
 
const STATUS_LABELS = {
  TODO: "To Do",
  IN_PROGRESS: "In Progress",
  HOLD: "Hold",
  REVIEW: "Review",
  DONE: "Done",
};
 
const STATUS_FLOW = {
  TODO: ["IN_PROGRESS"],
  IN_PROGRESS: ["REVIEW", "HOLD"],
  REVIEW: ["DONE", "IN_PROGRESS"],
  HOLD: ["IN_PROGRESS"],
  DONE: [],
};
 
export default function TaskDrawer({
  open,
  task,
  onClose,
  onStatusChange,
}) {
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showLateModal, setShowLateModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
 
  // 👇 NEW: track subtask progress
  const [subtaskProgress, setSubtaskProgress] = useState({
    total: 0,
    completed: 0,
  });
 
  useEffect(() => {
    setError(null);
    setSubtaskProgress({ total: 0, completed: 0 });
  }, [task?.id]);
 
  if (!open || !task) return null;
 
  const allowedStatuses = STATUS_FLOW[task.status] || [];
 
  const allSubtasksDone =
    subtaskProgress.total > 0 &&
    subtaskProgress.completed === subtaskProgress.total;
 
  // const isStatusLocked =
  //   subtaskProgress.total > 0 && !allSubtasksDone;
 
  const hasSubtasks = subtaskProgress.total > 0;
 
const isStatusLocked = hasSubtasks && !allSubtasksDone;
 
 
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === task.status) return;

    setUpdating(true);
    setError(null);

    try {
      // 🔥 If moving to HOLD, show hold modal first
      if (newStatus === "HOLD") {
        setPendingStatus(newStatus);
        setShowHoldModal(true);
        setUpdating(false);
        return;
      }

      // 🔥 If moving to DONE and it's late, show modal first
      if (newStatus === "DONE" && task.due_date) {
        const dueDate = new Date(`${getDateInputValue(task.due_date)}T00:00:00`);
        dueDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (today > dueDate) {
          setPendingStatus(newStatus);
          setShowLateModal(true);
          setUpdating(false);
          return;
        }
      }

      await proceedStatusUpdate(newStatus);
    } catch (err) {
      setError(err.message || "Status change not allowed");
    } finally {
      setUpdating(false);
    }
  };

  const proceedStatusUpdate = async (newStatus, completionReason = null, holdReason = null) => {
    setUpdating(true);
    try {
      const res = await updateTaskStatusApi(task.id, {
        status: newStatus,
        completion_reason: completionReason,
        reason: holdReason
      });

      if (!res?.data?.id) {
        throw new Error("Invalid API response");
      }

      onStatusChange(res.data);
    } catch (err) {
      setError(err.message || "Status change not allowed");
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmLate = (reason) => {
    setShowLateModal(false);
    proceedStatusUpdate(pendingStatus, reason, null);
  };

  const handleConfirmHold = (reason) => {
    setShowHoldModal(false);
    proceedStatusUpdate(pendingStatus, null, reason);
  };
 
 
 
 
 
  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* OVERLAY */}
      <div
        className="flex-1 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
 
      {/* DRAWER */}
      <aside className="w-full max-w-[460px] bg-[#f5f7fb] shadow-2xl flex flex-col overflow-y-auto">
 
 
 
<header className="sticky top-0 z-20 border-b border-white/10 bg-gradient-to-br from-[#172554] via-[#241556] to-[#120823] px-6 pb-6 pt-5 text-white shadow-xl">
  <div className="mb-5 flex items-start justify-between gap-4 pr-9">
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-200/75">
        <ListChecks size={14} />
        Task Card
      </div>
      <h2 className="break-words text-2xl font-black leading-tight tracking-tight">
        {task.title}
      </h2>
    </div>
 
    {/* 🔒 Blocked Badge */}
    {subtaskProgress.total > 0 && !allSubtasksDone && (
      <span className="shrink-0 rounded-full border border-orange-200/30 bg-orange-300/15 px-3 py-1 text-[10px] font-black uppercase tracking-wide text-orange-100">
        {/* Blocked */}
        {STATUS_LABELS[task.status]}
      </span>
    )}
  </div>
 
  <button
    onClick={onClose}
    className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/10 text-white/75 transition hover:bg-white/20 hover:text-white"
    aria-label="Close task details"
  >
    <X className="h-4 w-4" />
  </button>
</header>
 
 
        {/* STATUS + META */}
        <div className="space-y-4 px-5 py-5 text-sm">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">
              Status
            </label>
 
<select
  value={task.status}
  onChange={handleStatusChange}
  disabled={
    updating ||
    allowedStatuses.length === 0 ||
    isStatusLocked
  }
  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
>
  {/* CURRENT STATUS */}
  <option value={task.status}>
    {STATUS_LABELS[task.status]}
  </option>
 
  {/* NEXT ALLOWED STATUSES */}
  {allowedStatuses.map((s) => (
    <option key={s} value={s}>
      {STATUS_LABELS[s]}
    </option>
  ))}
</select>
 
 
 
            {subtaskProgress.total > 0 && (
  <div className="mt-2">
    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
      <div
        className="h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 transition-all"
        style={{
          width: `${
            (subtaskProgress.completed /
              subtaskProgress.total) *
            100
          }%`,
        }}
      />
    </div>
    <p className="mt-2 text-xs font-semibold text-slate-500">
      {subtaskProgress.completed} of{" "}
      {subtaskProgress.total} subtasks completed
    </p>
  </div>
)}
 
 
            {/* 👇 NEW UX MESSAGE */}
            {isStatusLocked && (
              <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700">
                Complete all subtasks to move this task forward
              </div>
            )}
          </div>
 
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-rose-600">
              <Flag size={13} />
              {task.priority}
            </span>
          </div>









          {task.due_date && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">Due</span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-600">
                  <CalendarRange className="w-3 h-3" />
                  {formatDisplayDate(task.due_date, "--")}
                </span>
                
                {/* 🔥 Immediate Overdue Indicator */}
                {(new Date(`${getDateInputValue(task.due_date)}T00:00:00`) < new Date().setHours(0,0,0,0)) && task.status !== 'DONE' && task.status !== 'HOLD' && (
                  <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-red-600 animate-pulse">
                    Overdue
                  </span>
                )}
              </div>
            </div>
          )}




 
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600">
              {error}
            </div>
          )}
          </section>
        </div>
 
        {/* ===== SUBTASKS ===== */}
        <div className="px-5">
          <TaskSubtasks
            taskId={task.id}
            onProgressChange={setSubtaskProgress}
          />
        </div>
 
 
{/* ===== COMMENTS ===== */}
<div className="px-5 pt-4">
  <TaskComments taskId={task.id} />
</div>

        {/* ===== ACTIVITY TIMELINE ===== */}
        {/* <TaskActivity taskId={task.id} /> */}
        {/* <div className="bg-[#EDE4F9] rounded-xl shadow-md p-4"> */}
<div className="px-5 py-5">
  <TaskActivity2 taskId={task.id} />
</div>
{/* </div> */}
      </aside>

      <LateCompletionModal
        isOpen={showLateModal}
        onClose={() => setShowLateModal(false)}
        onConfirm={handleConfirmLate}
        taskTitle={task.title}
      />

      <HoldReasonModal
        isOpen={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        onConfirm={handleConfirmHold}
        taskTitle={task.title}
      />
    </div>,
          document.body
  );
}
